import { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import { useIsMobile } from '../hooks/useIsMobile'
import SiteAdminEmailTab from '../components/SiteAdminEmailTab'

const BLUE = '#185FA5'
const th = { padding: '9px 16px', textAlign: 'left', fontWeight: 600, color: '#555', fontSize: 13 }
const TABS = ['overview', 'clubs', 'coaches', 'fixtures', 'email']
const TAB_LABELS = { overview: 'Overview', clubs: 'Clubs', squads: 'Squads', coaches: 'Coaches', fixtures: 'Fixtures', email: 'Email' }

export default function SiteAdmin({ profile }) {
  const [tab, setTab] = useState('overview')

  if (!profile?.is_site_admin) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
        <div style={{ fontSize: 18, fontWeight: 600 }}>Access Denied</div>
        <div style={{ marginTop: 8, fontSize: 14 }}>You don't have site admin access.</div>
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 16px 60px' }}>
      <div style={{ display: 'flex', alignItems: 'center', padding: '20px 0 16px', borderBottom: '2px solid ' + BLUE, marginBottom: 24 }}>
        <img src="/logo.svg" alt="GAA Match Tracker" style={{ height: 128, marginRight: 32 }} />
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: BLUE }}>Site Admin</div>
          <div style={{ fontSize: 13, color: '#666' }}>{profile.full_name || profile.email}</div>
        </div>
        <button
          onClick={() => window.history.back()}
          style={{ marginLeft: 'auto', padding: '8px 16px', background: 'transparent', border: '1px solid #ccc', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}
        >
          ← Back to App
        </button>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 28, flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: '9px 20px',
              background: tab === t ? BLUE : '#f0f0f0',
              color: tab === t ? '#fff' : '#333',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: tab === t ? 600 : 400,
              fontSize: 14,
            }}
          >
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab onNavigate={setTab} />}
      {tab === 'clubs' && <ClubsTab />}
      {tab === 'squads' && <SquadsTab />}
      {tab === 'coaches' && <CoachesTab />}
      {tab === 'fixtures' && <FixturesTab />}
      {tab === 'email' && <EmailTab />}
    </div>
  )
}

function StatCard({ label, value, color, onClick }) {
  return (
    <div onClick={onClick} style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: '18px 22px', flex: 1, minWidth: 130, cursor: onClick ? 'pointer' : 'default' }}>
      <div style={{ fontSize: 30, fontWeight: 700, color: color || BLUE }}>{value ?? '—'}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#444', marginTop: 4 }}>{label}</div>
    </div>
  )
}

function SectionLabel({ children }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
      {children}
    </div>
  )
}

function OverviewTab({ onNavigate }) {
  const [stats, setStats] = useState(null)
  const [recentClubs, setRecentClubs] = useState([])
  const [liveFixtures, setLiveFixtures] = useState([])
  const [todayFixtures, setTodayFixtures] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => { loadData() }, [])

  async function loadData() {
    setLoading(true)
    setError(null)
    try {
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()

      const [
        { count: clubCount },
        { count: squadCount },
        { count: userCount },
        { count: playerCount },
        { count: liveCount },
        { count: newClubs7d },
        { count: newClubs30d },
      ] = await Promise.all([
        supabase.from('clubs').select('*', { count: 'exact', head: true }),
        supabase.from('squads').select('*', { count: 'exact', head: true }),
        supabase.from('profiles').select('*', { count: 'exact', head: true }),
        supabase.from('players').select('*', { count: 'exact', head: true }),
        supabase.from('fixtures').select('*', { count: 'exact', head: true }).eq('status', 'live')
          .or('clock_running.eq.true,match_phase.in.(playing,ht,et1,et_ht,et2)'),
        supabase.from('clubs').select('*', { count: 'exact', head: true }).gte('created_at', sevenDaysAgo),
        supabase.from('clubs').select('*', { count: 'exact', head: true }).gte('created_at', thirtyDaysAgo),
      ])

      setStats({ clubCount, squadCount, userCount, playerCount, liveCount, newClubs7d, newClubs30d })

      const { data: liveData } = await supabase
        .from('fixtures')
        .select('id, opponent, home_score, away_score, squad_id, club_id')
        .eq('status', 'live')
        .or('clock_running.eq.true,match_phase.in.(playing,ht,et1,et_ht,et2)')
      
      // Fetch squad slugs separately
      const squadIds = (liveData || []).map(f => f.squad_id).filter(Boolean)
      const { data: squadData } = squadIds.length ? await supabase
        .from('squads')
        .select('id, name, slug, clubs!squads_club_id_fkey(name)')
        .in('id', squadIds) : { data: [] }
      const squadMap = {}
      ;(squadData || []).forEach(s => { squadMap[s.id] = s })
      const enrichedLive = (liveData || []).map(f => ({
        ...f,
        squads: squadMap[f.squad_id] || null,
        clubs: squadMap[f.squad_id]?.clubs || null,
      }))
      setLiveFixtures(enrichedLive)
      const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0)
      const todayEnd = new Date(); todayEnd.setHours(23, 59, 59, 999)
      const { data: todayData } = await supabase
        .from('fixtures')
        .select('id, opponent, fixture_date, squad_id, club_id, status')
        .gte('fixture_date', todayStart.toISOString())
        .lte('fixture_date', todayEnd.toISOString())
        .neq('status', 'completed')
      const todaySquadIds = (todayData || []).map(f => f.squad_id).filter(Boolean)
      const { data: todaySquadData } = todaySquadIds.length ? await supabase
        .from('squads')
        .select('id, name, slug, clubs!squads_club_id_fkey(name)')
        .in('id', todaySquadIds) : { data: [] }
      const todaySquadMap = {}
      ;(todaySquadData || []).forEach(s => { todaySquadMap[s.id] = s })
      const enrichedToday = (todayData || []).map(f => ({
        ...f,
        squads: todaySquadMap[f.squad_id] || null,
        clubs: todaySquadMap[f.squad_id]?.clubs || null,
      }))
      setTodayFixtures(enrichedToday)

      const { data: clubs, error: clubErr } = await supabase
        .from('clubs')
        .select('id, name, county, created_at')
        .order('created_at', { ascending: false })
        .limit(15)

      if (clubErr) throw clubErr
      setRecentClubs(clubs || [])
    } catch (err) {
      setError('Failed to load. Check that site admin RLS policies have been applied. Error: ' + (err.message || err))
    }
    setLoading(false)
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>Loading...</div>
  if (error) return (
    <div style={{ padding: 16, background: '#fff3f3', border: '1px solid #f5c6cb', borderRadius: 10, color: '#c0392b', fontSize: 14 }}>
      {error}
    </div>
  )

  return (
    <div>
      {stats?.liveCount > 0 && (
        <div style={{ background: '#e8f5e9', border: '1px solid #81c784', borderRadius: 10, marginBottom: 24, overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>🔴</span>
            <span style={{ fontWeight: 600, color: '#2e7d32' }}>
              {stats.liveCount} match{stats.liveCount !== 1 ? 'es' : ''} live right now
            </span>
          </div>
          <div style={{ pointerEvents: 'auto' }}>
          {liveFixtures.map(function(f) {
            var squadSlug = f.squads ? f.squads.slug : ''
            var clubName = f.clubs ? f.clubs.name : ''
            var squadName = f.squads ? f.squads.name : ''
            var publicUrl = squadSlug ? '/public/' + squadSlug : null
            var homeScore = f.home_score != null ? f.home_score : '0'
            var awayScore = f.away_score != null ? f.away_score : '0'
            return (
              <a
                key={f.id}
                href={publicUrl || '#'}
                target={publicUrl ? '_blank' : undefined}
                rel="noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '10px 16px',
                  borderTop: '1px solid #a5d6a7',
                  textDecoration: 'none',
                  background: '#f1f8f1',
                  opacity: publicUrl ? 1 : 0.5,
                  cursor: publicUrl ? 'pointer' : 'default',
                }}
              >
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 600, color: '#1b5e20', fontSize: 14 }}>
                    {clubName} — {squadName}
                  </span>
                  <span style={{ color: '#555', fontSize: 14 }}> vs {f.opponent || 'Unknown'}</span>
                </div>
                <div style={{ fontWeight: 700, color: '#2e7d32', fontSize: 15, marginRight: 12 }}>
                  {homeScore} – {awayScore}
                </div>
                <span style={{ fontSize: 13, color: '#27ae60' }}>View →</span>
              </a>
            )
          })}
          </div>
        </div>
      )}

      {todayFixtures.length > 0 && (
        <div style={{ background: '#e6f1fb', border: '1px solid #90b8e0', borderRadius: 10, marginBottom: 24, overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>📅</span>
            <span style={{ fontWeight: 600, color: '#185FA5' }}>
              {todayFixtures.length} fixture{todayFixtures.length !== 1 ? 's' : ''} today
            </span>
          </div>
          <div>
          {todayFixtures.map(function(f) {
            var squadSlug = f.squads ? f.squads.slug : ''
            var clubName = f.clubs ? f.clubs.name : ''
            var squadName = f.squads ? f.squads.name : ''
            var publicUrl = squadSlug ? '/public/' + squadSlug : null
            var timeStr = f.fixture_date ? new Date(f.fixture_date).toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }) : ''
            return (
              <a
                key={f.id}
                href={publicUrl || '#'}
                target={publicUrl ? '_blank' : undefined}
                rel="noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '10px 16px',
                  borderTop: '1px solid #b8d3ec',
                  textDecoration: 'none',
                  background: '#f2f7fc',
                  opacity: publicUrl ? 1 : 0.5,
                  cursor: publicUrl ? 'pointer' : 'default',
                }}
              >
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 600, color: '#185FA5', fontSize: 14 }}>
                    {clubName} — {squadName}
                  </span>
                  <span style={{ color: '#555', fontSize: 14 }}> vs {f.opponent || 'Unknown'}</span>
                </div>
                <div style={{ fontWeight: 600, color: '#185FA5', fontSize: 13, marginRight: 12 }}>
                  {timeStr}
                </div>
                <span style={{ fontSize: 13, color: '#185FA5' }}>View →</span>
              </a>
            )
          })}
          </div>
        </div>
      )}
      <SectionLabel>Platform totals</SectionLabel>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 28 }}>
        <StatCard label="Clubs" value={stats?.clubCount} onClick={() => onNavigate('clubs')} />
        <StatCard label="Squads" value={stats?.squadCount} onClick={() => onNavigate('squads')} />
        <StatCard label="Users" value={stats?.userCount} onClick={() => onNavigate('coaches')} />
        <StatCard label="Players" value={stats?.playerCount} />
      </div>

      <SectionLabel>New signups</SectionLabel>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 32 }}>
        <StatCard label="New clubs (7 days)" value={stats?.newClubs7d} color="#27ae60" />
        <StatCard label="New clubs (30 days)" value={stats?.newClubs30d} color="#27ae60" />
      </div>

      <SectionLabel>Recently joined clubs</SectionLabel>
      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#f5f7fa' }}>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: '#555', fontSize: 13 }}>Club</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: '#555', fontSize: 13 }}>County</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: '#555', fontSize: 13 }}>Joined</th>
            </tr>
          </thead>
          <tbody>
            {recentClubs.length === 0 ? (
              <tr><td colSpan={3} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No clubs yet</td></tr>
            ) : recentClubs.map((club, i) => (
              <tr key={club.id} style={{ borderTop: i === 0 ? 'none' : '1px solid #f0f0f0' }}>
                <td style={{ padding: '11px 16px', fontWeight: 500 }}>{club.name}</td>
                <td style={{ padding: '11px 16px', color: '#666' }}>{club.county || '—'}</td>
                <td style={{ padding: '11px 16px', color: '#999', fontSize: 13 }}>
                  {new Date(club.created_at).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function ClubsTab() {
  const isMobile = useIsMobile()
  const [clubs, setClubs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [countyFilter, setCountyFilter] = useState('')
  const [selectedClub, setSelectedClub] = useState(null)

  useEffect(() => { loadClubs() }, [])

  async function loadClubs() {
    setLoading(true)
    setError(null)
    try {
      const { data, error } = await supabase
        .from('clubs')
        .select(`
          id, name, county, slug, created_at, features,
          squads(id),
          profiles!profiles_club_id_fkey(id, full_name, email, role)
        `)
        .order('created_at', { ascending: false })
      if (error) throw error
      setClubs(data || [])
    } catch (err) {
      setError('Failed to load clubs: ' + (err.message || err))
    }
    setLoading(false)
  }

  const counties = [...new Set(clubs.map(c => c.county).filter(Boolean))].sort()

  const filtered = clubs.filter(c => {
    const matchSearch = !search ||
      c.name?.toLowerCase().includes(search.toLowerCase()) ||
      c.county?.toLowerCase().includes(search.toLowerCase())
    const matchCounty = !countyFilter || c.county === countyFilter
    return matchSearch && matchCounty
  })

  if (selectedClub) {
    return <ClubDetail club={selectedClub} onBack={() => { setSelectedClub(null); loadClubs() }} />
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>Loading...</div>
  if (error) return <div style={{ padding: 16, background: '#fff3f3', border: '1px solid #f5c6cb', borderRadius: 10, color: '#c0392b', fontSize: 14 }}>{error}</div>

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <input
          placeholder="Search clubs..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 2, minWidth: 180, padding: '9px 14px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, outline: 'none' }}
        />
        <select
          value={countyFilter}
          onChange={e => setCountyFilter(e.target.value)}
          style={{ flex: 1, minWidth: 140, padding: '9px 14px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, background: '#fff' }}
        >
          <option value="">All counties</option>
          {counties.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <div style={{ display: 'flex', alignItems: 'center', fontSize: 13, color: '#888', whiteSpace: 'nowrap' }}>
          {filtered.length} club{filtered.length !== 1 ? 's' : ''}
        </div>
      </div>

      {isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No clubs found</div>
          ) : filtered.map(club => {
            const coachCount = (club.profiles || []).filter(p => p.role !== 'player').length
            return (
              <div key={club.id} style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: BLUE }}>{club.name}</div>
                  <button
                    onClick={() => setSelectedClub(club)}
                    style={{ padding: '5px 12px', background: BLUE, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}
                  >View →</button>
                </div>
                <div style={{ fontSize: 13, color: '#666', display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
                  <span>📍 {club.county || '—'}</span>
                  <span>🏃 {(club.squads || []).length} squad{(club.squads || []).length !== 1 ? 's' : ''}</span>
                  <span>👤 {coachCount} coach{coachCount !== 1 ? 'es' : ''}</span>
                  <span>📅 {new Date(club.created_at).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                </div>
                <div style={{ fontSize: 11, color: club.slug ? '#999' : '#e74c3c', fontFamily: 'monospace', marginTop: 6 }}>
                  {club.slug || 'slug missing'}
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f5f7fa' }}>
                <th style={th}>Club</th>
                <th style={th}>County</th>
                <th style={th}>Squads</th>
                <th style={th}>Coaches</th>
                <th style={th}>Joined</th>
                <th style={th}>Slug</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No clubs found</td></tr>
              ) : filtered.map((club, i) => {
                const coachCount = (club.profiles || []).filter(p => p.role !== 'player').length
                return (
                  <tr key={club.id} style={{ borderTop: i === 0 ? 'none' : '1px solid #f0f0f0' }}>
                    <td style={{ padding: '11px 16px', fontWeight: 600 }}>{club.name}</td>
                    <td style={{ padding: '11px 16px', color: '#666' }}>{club.county || '—'}</td>
                    <td style={{ padding: '11px 16px', color: '#666' }}>{(club.squads || []).length}</td>
                    <td style={{ padding: '11px 16px', color: '#666' }}>{coachCount}</td>
                    <td style={{ padding: '11px 16px', color: '#999', fontSize: 13 }}>
                      {new Date(club.created_at).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                    <td style={{ padding: '11px 16px', color: '#999', fontSize: 12, fontFamily: 'monospace' }}>
                      {club.slug || <span style={{ color: '#e74c3c' }}>missing</span>}
                    </td>
                    <td style={{ padding: '11px 16px' }}>
                      <button
                        onClick={() => setSelectedClub(club)}
                        style={{ padding: '5px 12px', background: BLUE, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap' }}
                      >View →</button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function ClubDetail({ club, onBack }) {
  const [squads, setSquads] = useState([])
  const [coaches, setCoaches] = useState([])
  const [fixtures, setFixtures] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => { loadDetail() }, [])

  async function loadDetail() {
    setLoading(true)
    const [
      { data: squadsData },
      { data: coachesData },
      { data: fixturesData },
    ] = await Promise.all([
      supabase.from('squads').select('id, name, sport, age_group, gender, created_at').eq('club_id', club.id).order('created_at'),
      supabase.from('profiles').select('id, full_name, email, role').eq('club_id', club.id).order('full_name'),
      supabase.from('fixtures').select('id, date, home_score, away_score, status, opponent').eq('club_id', club.id).order('date', { ascending: false }).limit(10),
    ])
    setSquads(squadsData || [])
    setCoaches(coachesData || [])
    setFixtures(fixturesData || [])
    setLoading(false)
  }

  return (
    <div>
      <button
        onClick={onBack}
        style={{ marginBottom: 20, padding: '8px 16px', background: '#f0f0f0', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}
      >
        ← Back to clubs
      </button>

      <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 24, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: BLUE }}>{club.name}</div>
          <div style={{ fontSize: 14, color: '#666', marginTop: 2 }}>
            {club.county || 'No county'} · Slug: <span style={{ fontFamily: 'monospace', fontSize: 13 }}>{club.slug || <span style={{ color: '#e74c3c' }}>missing</span>}</span>
          </div>
          <div style={{ fontSize: 13, color: '#999', marginTop: 2 }}>
            Joined {new Date(club.created_at).toLocaleDateString('en-IE', { day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
        </div>
        
        <a
          href={'/public/club/' + club.slug}
          target="_blank"
          rel="noreferrer"
          style={{ marginLeft: 'auto', padding: '8px 14px', background: '#e8f5e9', color: '#27ae60', border: '1px solid #81c784', borderRadius: 8, fontSize: 13, textDecoration: 'none', whiteSpace: 'nowrap' }}
        >
          View public page ↗
        </a>
      </div>

      {loading ? <div style={{ padding: 24, textAlign: 'center', color: '#999' }}>Loading...</div> : (
        <div>
          <SectionLabel>Squads ({squads.length})</SectionLabel>
          <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, overflow: 'hidden', marginBottom: 24 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: '#f5f7fa' }}>
                  <th style={th}>Name</th>
                  <th style={th}>Sport</th>
                  <th style={th}>Age Group</th>
                  <th style={th}>Gender</th>
                  <th style={th}>Created</th>
                </tr>
              </thead>
              <tbody>
                {squads.length === 0 ? (
                  <tr><td colSpan={5} style={{ padding: 16, textAlign: 'center', color: '#aaa' }}>No squads yet</td></tr>
                ) : squads.map((s, i) => (
                  <tr key={s.id} style={{ borderTop: i === 0 ? 'none' : '1px solid #f0f0f0' }}>
                    <td style={{ padding: '10px 16px', fontWeight: 500 }}>{s.name}</td>
                    <td style={{ padding: '10px 16px', color: '#666' }}>{s.sport || '—'}</td>
                    <td style={{ padding: '10px 16px', color: '#666' }}>{s.age_group || '—'}</td>
                    <td style={{ padding: '10px 16px', color: '#666' }}>{s.gender || '—'}</td>
                    <td style={{ padding: '10px 16px', color: '#999', fontSize: 13 }}>
                      {new Date(s.created_at).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <SectionLabel>Coaches & Admins ({coaches.length})</SectionLabel>
          <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, overflow: 'hidden', marginBottom: 24 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: '#f5f7fa' }}>
                  <th style={th}>Name</th>
                  <th style={th}>Email</th>
                  <th style={th}>Role</th>
                </tr>
              </thead>
              <tbody>
                {coaches.length === 0 ? (
                  <tr><td colSpan={3} style={{ padding: 16, textAlign: 'center', color: '#aaa' }}>No coaches yet</td></tr>
                ) : coaches.map((c, i) => (
                  <tr key={c.id} style={{ borderTop: i === 0 ? 'none' : '1px solid #f0f0f0' }}>
                    <td style={{ padding: '10px 16px', fontWeight: 500 }}>{c.full_name || '—'}</td>
                    <td style={{ padding: '10px 16px', color: '#666' }}>{c.email}</td>
                    <td style={{ padding: '10px 16px' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 600,
                        background: c.role === 'club_admin' ? '#e3f2fd' : '#f3e5f5',
                        color: c.role === 'club_admin' ? '#1565c0' : '#6a1b9a'
                      }}>
                        {c.role}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <SectionLabel>Recent fixtures ({fixtures.length > 0 ? 'last 10' : '0'})</SectionLabel>
          <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: '#f5f7fa' }}>
                  <th style={th}>Date</th>
                  <th style={th}>Opponent</th>
                  <th style={th}>Score</th>
                  <th style={th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {fixtures.length === 0 ? (
                  <tr><td colSpan={4} style={{ padding: 16, textAlign: 'center', color: '#aaa' }}>No fixtures yet</td></tr>
                ) : fixtures.map((f, i) => (
                  <tr key={f.id} style={{ borderTop: i === 0 ? 'none' : '1px solid #f0f0f0' }}>
                    <td style={{ padding: '10px 16px', color: '#666', fontSize: 13 }}>
                      {f.date ? new Date(f.date).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                    </td>
                    <td style={{ padding: '10px 16px', fontWeight: 500 }}>{f.opponent || '—'}</td>
                    <td style={{ padding: '10px 16px', color: '#666' }}>
                      {f.home_score != null ? (f.home_score + ' – ' + f.away_score) : '—'}
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 600,
                        background: f.status === 'live' ? '#ffebee' : f.status === 'completed' ? '#e8f5e9' : '#f5f5f5',
                        color: f.status === 'live' ? '#c62828' : f.status === 'completed' ? '#2e7d32' : '#757575'
                      }}>
                        {f.status || 'scheduled'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

function SquadsTab() {
  const isMobile = useIsMobile()
  const [squads, setSquads] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  useEffect(() => { loadSquads() }, [])
  async function loadSquads() {
    setLoading(true)
    setError(null)
    try {
      const { data: squadData, error: squadErr } = await supabase
        .from('squads')
        .select('id, name, sport, age_group, gender, slug, club_id, clubs!squads_club_id_fkey(name)')
        .order('created_at', { ascending: false })
      if (squadErr) throw squadErr
      const squadIds = (squadData || []).map(s => s.id)
      const { data: playerData } = squadIds.length ? await supabase
        .from('players').select('id, squad_id').in('squad_id', squadIds) : { data: [] }
      const { data: fixtureData } = squadIds.length ? await supabase
        .from('fixtures').select('id, squad_id').in('squad_id', squadIds) : { data: [] }
      const { data: memberData } = squadIds.length ? await supabase
        .from('squad_members').select('squad_id, role, profiles!squad_members_profile_id_fkey(full_name)').in('squad_id', squadIds).eq('role', 'squad_admin') : { data: [] }
      const playerCounts = {}
      ;(playerData || []).forEach(p => { playerCounts[p.squad_id] = (playerCounts[p.squad_id] || 0) + 1 })
      const fixtureCounts = {}
      ;(fixtureData || []).forEach(f => { fixtureCounts[f.squad_id] = (fixtureCounts[f.squad_id] || 0) + 1 })
      const adminsBySquad = {}
      ;(memberData || []).forEach(m => {
        if (!adminsBySquad[m.squad_id]) adminsBySquad[m.squad_id] = []
        adminsBySquad[m.squad_id].push(m.profiles?.full_name || 'Unknown')
      })
      const enriched = (squadData || []).map(s => ({
        ...s,
        playerCount: playerCounts[s.id] || 0,
        fixtureCount: fixtureCounts[s.id] || 0,
        admins: adminsBySquad[s.id] || []
      }))
      setSquads(enriched)
    } catch (err) {
      setError('Failed to load squads: ' + (err.message || err))
    }
    setLoading(false)
  }
  const filtered = squads.filter(s => {
    return !search ||
      s.name?.toLowerCase().includes(search.toLowerCase()) ||
      s.clubs?.name?.toLowerCase().includes(search.toLowerCase())
  })
  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>Loading...</div>
  if (error) return <div style={{ padding: 16, background: '#fff3f3', border: '1px solid #f5c6cb', borderRadius: 10, color: '#c0392b', fontSize: 14 }}>{error}</div>
  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <input
          placeholder="Search squad or club..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 2, minWidth: 200, padding: '9px 14px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, outline: 'none' }}
        />
        <div style={{ display: 'flex', alignItems: 'center', fontSize: 13, color: '#888', whiteSpace: 'nowrap' }}>
          {filtered.length} squad{filtered.length !== 1 ? 's' : ''}
        </div>
      </div>
      {isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No squads found</div>
          ) : filtered.map(s => (
            <div key={s.id} style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: 14 }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{s.name}</div>
              <div style={{ fontSize: 13, color: '#666', marginBottom: 6 }}>{s.clubs?.name || '—'}</div>
              <div style={{ fontSize: 13, color: '#666', display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
                <span>{s.sport}</span>
                <span>{s.age_group || '—'}</span>
                <span>{s.gender}</span>
              </div>
              <div style={{ fontSize: 13, color: '#666', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <span>👥 {s.playerCount} players</span>
                <span>📅 {s.fixtureCount} fixtures</span>
                <span>{s.slug ? '🔗 Public page' : '⚠️ No public page'}</span>
              </div>
              {s.admins.length > 0 && <div style={{ fontSize: 12, color: '#999', marginTop: 6 }}>Admin: {s.admins.join(', ')}</div>}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f5f7fa' }}>
                <th style={th}>Squad</th>
                <th style={th}>Club</th>
                <th style={th}>Sport</th>
                <th style={th}>Age Group</th>
                <th style={th}>Players</th>
                <th style={th}>Fixtures</th>
                <th style={th}>Squad Admin</th>
                <th style={th}>Public Page</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No squads found</td></tr>
              ) : filtered.map((s, i) => (
                <tr key={s.id} style={{ borderTop: i === 0 ? 'none' : '1px solid #f0f0f0' }}>
                  <td style={{ padding: '11px 16px', fontWeight: 500 }}>{s.name}</td>
                  <td style={{ padding: '11px 16px', color: '#666' }}>{s.clubs?.name || '—'}</td>
                  <td style={{ padding: '11px 16px', color: '#666' }}>{s.sport}</td>
                  <td style={{ padding: '11px 16px', color: '#666' }}>{s.age_group || '—'}</td>
                  <td style={{ padding: '11px 16px', color: '#666' }}>{s.playerCount}</td>
                  <td style={{ padding: '11px 16px', color: '#666' }}>{s.fixtureCount}</td>
                  <td style={{ padding: '11px 16px', color: '#666' }}>{s.admins.join(', ') || '—'}</td>
                  <td style={{ padding: '11px 16px' }}>
                    {s.slug ? <span style={{ color: '#2e7d32' }}>✓</span> : <span style={{ color: '#c62828' }}>✗</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
function CoachesTab() {
  const isMobile = useIsMobile()
  const [coaches, setCoaches] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('')

  useEffect(() => { loadCoaches() }, [])

  async function loadCoaches() {
    setLoading(true)
    setError(null)
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, email, role, is_site_admin, created_at, clubs!profiles_club_id_fkey(name, county)')
        .order('created_at', { ascending: false })
      if (error) throw error
      setCoaches(data || [])
    } catch (err) {
      setError('Failed to load coaches: ' + (err.message || err))
    }
    setLoading(false)
  }

  const filtered = coaches.filter(c => {
    const matchSearch = !search ||
      c.full_name?.toLowerCase().includes(search.toLowerCase()) ||
      c.email?.toLowerCase().includes(search.toLowerCase()) ||
      c.clubs?.name?.toLowerCase().includes(search.toLowerCase())
    const matchRole = !roleFilter || c.role === roleFilter
    return matchSearch && matchRole
  })

  const roles = [...new Set(coaches.map(c => c.role).filter(Boolean))].sort()

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>Loading...</div>
  if (error) return <div style={{ padding: 16, background: '#fff3f3', border: '1px solid #f5c6cb', borderRadius: 10, color: '#c0392b', fontSize: 14 }}>{error}</div>

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <input
          placeholder="Search name, email or club..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 2, minWidth: 200, padding: '9px 14px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, outline: 'none' }}
        />
        <select
          value={roleFilter}
          onChange={e => setRoleFilter(e.target.value)}
          style={{ flex: 1, minWidth: 140, padding: '9px 14px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, background: '#fff' }}
        >
          <option value="">All roles</option>
          {roles.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <div style={{ display: 'flex', alignItems: 'center', fontSize: 13, color: '#888', whiteSpace: 'nowrap' }}>
          {filtered.length} user{filtered.length !== 1 ? 's' : ''}
        </div>
      </div>

      {isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No users found</div>
          ) : filtered.map(c => (
            <div key={c.id} style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>
                  {c.full_name || '—'}
                  {c.is_site_admin && <span style={{ marginLeft: 6, padding: '1px 6px', background: '#fff3e0', color: '#e65100', borderRadius: 6, fontSize: 11, fontWeight: 700 }}>SITE ADMIN</span>}
                </div>
                <span style={{
                  padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 600,
                  background: c.role === 'club_admin' ? '#e3f2fd' : c.role === 'squad_admin' ? '#f3e5f5' : '#f5f5f5',
                  color: c.role === 'club_admin' ? '#1565c0' : c.role === 'squad_admin' ? '#6a1b9a' : '#555'
                }}>{c.role || '—'}</span>
              </div>
              <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>{c.email}</div>
              <div style={{ fontSize: 13, color: '#666', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <span>🏟 {c.clubs?.name || '—'}</span>
                <span>📍 {c.clubs?.county || '—'}</span>
                <span>📅 {new Date(c.created_at).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f5f7fa' }}>
                <th style={th}>Name</th>
                <th style={th}>Email</th>
                <th style={th}>Club</th>
                <th style={th}>County</th>
                <th style={th}>Role</th>
                <th style={th}>Joined</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No users found</td></tr>
              ) : filtered.map((c, i) => (
                <tr key={c.id} style={{ borderTop: i === 0 ? 'none' : '1px solid #f0f0f0' }}>
                  <td style={{ padding: '11px 16px', fontWeight: 500 }}>
                    {c.full_name || '—'}
                    {c.is_site_admin && (
                      <span style={{ marginLeft: 6, padding: '1px 6px', background: '#fff3e0', color: '#e65100', borderRadius: 6, fontSize: 11, fontWeight: 700 }}>
                        SITE ADMIN
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '11px 16px', color: '#666' }}>{c.email}</td>
                  <td style={{ padding: '11px 16px', color: '#666' }}>{c.clubs?.name || '—'}</td>
                  <td style={{ padding: '11px 16px', color: '#666' }}>{c.clubs?.county || '—'}</td>
                  <td style={{ padding: '11px 16px' }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 600,
                      background: c.role === 'club_admin' ? '#e3f2fd' : c.role === 'squad_admin' ? '#f3e5f5' : '#f5f5f5',
                      color: c.role === 'club_admin' ? '#1565c0' : c.role === 'squad_admin' ? '#6a1b9a' : '#555'
                    }}>
                      {c.role || '—'}
                    </span>
                  </td>
                  <td style={{ padding: '11px 16px', color: '#999', fontSize: 13 }}>
                    {new Date(c.created_at).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function FixturesTab() {
  const isMobile = useIsMobile()
  const [fixtures, setFixtures] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [subTab, setSubTab] = useState('upcoming')
  const [selectedFixture, setSelectedFixture] = useState(null)

  useEffect(() => { loadFixtures() }, [])

  async function loadFixtures() {
    setLoading(true)
    setError(null)
    try {
      const { data, error } = await supabase
        .from('fixtures')
        .select(`
          id, opponent, fixture_date, status, sport, venue,
          home_goals, home_points, away_goals, away_points,
          match_phase, clock_half, clock_running, clock_started_at,
          clock_seconds_at_start, season, game_type,
          squads!fixtures_squad_id_fkey(name),
          clubs!fixtures_club_id_fkey(name, slug)
        `)
        .order('fixture_date', { ascending: false })
        .limit(500)
      if (error) throw error
      setFixtures(data || [])
    } catch (err) {
      setError('Failed to load fixtures: ' + (err.message || err))
    }
    setLoading(false)
  }

  const now = new Date()
  const sevenDaysAgo = new Date(now - 7 * 24 * 60 * 60 * 1000)

  const upcoming = fixtures
    .filter(f => f.status === 'upcoming' && new Date(f.fixture_date) >= sevenDaysAgo)
    .sort((a, b) => new Date(a.fixture_date) - new Date(b.fixture_date))

  const live = fixtures
    .filter(f => f.status === 'live')
    .sort((a, b) => new Date(b.fixture_date) - new Date(a.fixture_date))

  const results = fixtures
    .filter(f => f.status === 'completed')
    .sort((a, b) => new Date(b.fixture_date) - new Date(a.fixture_date))

  const overdue = fixtures
    .filter(f => f.status === 'upcoming' && new Date(f.fixture_date) < sevenDaysAgo)
    .sort((a, b) => new Date(b.fixture_date) - new Date(a.fixture_date))

  const subTabs = [
    { key: 'upcoming', label: 'Upcoming', count: upcoming.length },
    { key: 'live', label: 'Live', count: live.length },
    { key: 'results', label: 'Results', count: results.length },
    { key: 'overdue', label: 'Overdue', count: overdue.length },
  ]

  const currentList = subTab === 'upcoming' ? upcoming
    : subTab === 'live' ? live
    : subTab === 'results' ? results
    : overdue

  const filtered = currentList.filter(f => {
    if (!search) return true
    const s = search.toLowerCase()
    return f.opponent?.toLowerCase().includes(s) ||
      f.clubs?.name?.toLowerCase().includes(s) ||
      f.squads?.name?.toLowerCase().includes(s)
  })

  function formatScore(f) {
    if (f.home_goals == null) return '—'
    const hTotal = (f.home_goals * 3) + f.home_points
    const aTotal = (f.away_goals * 3) + f.away_points
    return f.home_goals + '-' + String(f.home_points).padStart(2, '0') +
      ' (' + hTotal + ')  v  ' +
      f.away_goals + '-' + String(f.away_points).padStart(2, '0') +
      ' (' + aTotal + ')'
  }

  function statusBadge(f) {
    if (f.status === 'live') return <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 600, background: '#ffebee', color: '#c62828' }}>🔴 Live</span>
    if (f.status === 'completed') return <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 600, background: '#e8f5e9', color: '#2e7d32' }}>✅ Completed</span>
    return <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 600, background: '#f5f5f5', color: '#757575' }}>Upcoming</span>
  }

  if (selectedFixture) {
    return <FixtureDetail fixture={selectedFixture} onBack={() => { setSelectedFixture(null); loadFixtures() }} />
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>Loading...</div>
  if (error) return <div style={{ padding: 16, background: '#fff3f3', border: '1px solid #f5c6cb', borderRadius: 10, color: '#c0392b', fontSize: 14 }}>{error}</div>

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, borderBottom: '1px solid #e0e0e0', paddingBottom: 0 }}>
        {subTabs.map(t => (
          <button
            key={t.key}
            onClick={() => setSubTab(t.key)}
            style={{
              padding: '8px 16px',
              background: 'none',
              border: 'none',
              borderBottom: subTab === t.key ? '2px solid ' + BLUE : '2px solid transparent',
              color: subTab === t.key ? BLUE : '#888',
              fontWeight: subTab === t.key ? 700 : 400,
              fontSize: 14,
              cursor: 'pointer',
              marginBottom: -1,
            }}
          >
            {t.label}
            <span style={{ marginLeft: 6, fontSize: 12, background: t.key === 'overdue' && t.count > 0 ? '#ffebee' : '#f0f0f0', color: t.key === 'overdue' && t.count > 0 ? '#c62828' : '#888', padding: '1px 6px', borderRadius: 10 }}>
              {t.count}
            </span>
          </button>
        ))}
      </div>

      <div style={{ marginBottom: 16 }}>
        <input
          placeholder="Search club, squad or opponent..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: '100%', padding: '9px 14px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
        />
      </div>

      {subTab === 'overdue' && overdue.length > 0 && (
        <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#f57f17' }}>
          ⚠️ These fixtures passed more than 7 days ago without being tracked. Useful for identifying inactive clubs.
        </div>
      )}

      {isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No fixtures found</div>
          ) : filtered.map(f => (
            <div key={f.id} style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>vs {f.opponent || '—'}</div>
                <button
                  onClick={() => setSelectedFixture(f)}
                  style={{ padding: '5px 12px', background: BLUE, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}
                >View →</button>
              </div>
              <div style={{ fontSize: 13, color: '#666', marginBottom: 6 }}>
                {f.clubs?.name || '—'} · {f.squads?.name || '—'}
              </div>
              <div style={{ fontSize: 13, color: '#999', marginBottom: 6 }}>
                📅 {f.fixture_date ? new Date(f.fixture_date).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
              </div>
              {subTab === 'results' && (
                <div style={{ fontSize: 14, fontWeight: 600, color: BLUE, marginBottom: 6 }}>
                  {formatScore(f)}
                </div>
              )}
              <div>{statusBadge(f)}</div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f5f7fa' }}>
                <th style={th}>Date</th>
                <th style={th}>Club</th>
                <th style={th}>Squad</th>
                <th style={th}>Opponent</th>
                <th style={th}>Score</th>
                <th style={th}>Status</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No fixtures found</td></tr>
              ) : filtered.map((f, i) => (
                <tr key={f.id} style={{ borderTop: i === 0 ? 'none' : '1px solid #f0f0f0' }}>
                  <td style={{ padding: '10px 16px', color: '#666', fontSize: 13, whiteSpace: 'nowrap' }}>
                    {f.fixture_date ? new Date(f.fixture_date).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                  </td>
                  <td style={{ padding: '10px 16px', color: '#666' }}>{f.clubs?.name || '—'}</td>
                  <td style={{ padding: '10px 16px', color: '#666' }}>{f.squads?.name || '—'}</td>
                  <td style={{ padding: '10px 16px', fontWeight: 500 }}>{f.opponent || '—'}</td>
                  <td style={{ padding: '10px 16px', color: '#666', fontSize: 13, whiteSpace: 'nowrap' }}>
                    {subTab === 'results' ? formatScore(f) : '—'}
                  </td>
                  <td style={{ padding: '10px 16px' }}>{statusBadge(f)}</td>
                  <td style={{ padding: '10px 16px' }}>
                    <button
                      onClick={() => setSelectedFixture(f)}
                      style={{ padding: '5px 12px', background: BLUE, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}
                    >View →</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function FixtureDetail({ fixture, onBack }) {
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [seconds, setSeconds] = useState(0)
  const [editing, setEditing] = useState(false)
  const [editStatus, setEditStatus] = useState(fixture.status)

  useEffect(() => {
    loadEvents()
    if (fixture.status === 'live') {
      if (fixture.clock_running && fixture.clock_started_at) {
        const startEpoch = new Date(fixture.clock_started_at).getTime()
        const base = fixture.clock_seconds_at_start || 0
        const tick = () => {
          const elapsed = Math.floor((Date.now() - startEpoch) / 1000)
          setSeconds(Math.min(base + elapsed, 3000))
        }
        tick()
        const interval = setInterval(tick, 1000)
        return () => clearInterval(interval)
      } else {
        setSeconds(fixture.clock_seconds_at_start || 0)
      }
    }
  }, [])

  async function loadEvents() {
    setLoading(true)
    const { data } = await supabase
      .from('match_events')
      .select('*, players(full_name)')
      .eq('fixture_id', fixture.id)
      .order('minute', { ascending: true })
    setEvents(data || [])
    setLoading(false)
  }

  async function saveStatus() {
    await supabase.from('fixtures').update({ status: editStatus }).eq('id', fixture.id)
    setEditing(false)
    fixture.status = editStatus
  }

  const clockDisplay = String(Math.floor(seconds / 60)).padStart(2, '0') + ':' + String(seconds % 60).padStart(2, '0')

  function formatScore(f) {
    if (f.home_goals == null) return null
    const hTotal = (f.home_goals * 3) + f.home_points
    const aTotal = (f.away_goals * 3) + f.away_points
    return {
      home: f.home_goals + '-' + String(f.home_points).padStart(2, '0'),
      away: f.away_goals + '-' + String(f.away_points).padStart(2, '0'),
      hTotal, aTotal
    }
  }

  const score = formatScore(fixture)
  const scoringEvents = events.filter(e => ['goal', 'point', 'two_pointer'].includes(e.event_type))
  const otherEvents = events.filter(e => !['goal', 'point', 'two_pointer'].includes(e.event_type))

  function eventLabel(type) {
    const labels = {
      goal: '⚽ Goal', point: '🏳 Point', two_pointer: '2️⃣ Two pointer',
      yellow_card: '🟡 Yellow card', red_card: '🔴 Red card',
      black_card: '⚫ Black card', sin_bin: '⏱ Sin bin',
      substitution: '🔄 Sub', wide: '↔ Wide', free: '🎯 Free won',
    }
    return labels[type] || type
  }

  return (
    <div>
      <button
        onClick={onBack}
        style={{ marginBottom: 20, padding: '8px 16px', background: '#f0f0f0', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}
      >
        ← Back to fixtures
      </button>

      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: 20, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: BLUE }}>
              {fixture.clubs?.name} — {fixture.squads?.name}
            </div>
            <div style={{ fontSize: 14, color: '#666', marginTop: 2 }}>
              vs {fixture.opponent || '—'} · {fixture.fixture_date ? new Date(fixture.fixture_date).toLocaleDateString('en-IE', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
            </div>
            {fixture.venue && (
              <div style={{ fontSize: 13, color: '#999', marginTop: 2 }}>📍 {fixture.venue}</div>
            )}
          </div>
          <div style={{ textAlign: 'right' }}>
            {editing ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <select
                  value={editStatus}
                  onChange={e => setEditStatus(e.target.value)}
                  style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }}
                >
                  <option value="upcoming">Upcoming</option>
                  <option value="live">Live</option>
                  <option value="completed">Completed</option>
                </select>
                <button onClick={saveStatus} style={{ padding: '6px 12px', background: BLUE, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Save</button>
                <button onClick={() => setEditing(false)} style={{ padding: '6px 12px', background: '#f0f0f0', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{
                  padding: '3px 10px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                  background: fixture.status === 'live' ? '#ffebee' : fixture.status === 'completed' ? '#e8f5e9' : '#f5f5f5',
                  color: fixture.status === 'live' ? '#c62828' : fixture.status === 'completed' ? '#2e7d32' : '#757575'
                }}>
                  {fixture.status || 'upcoming'}
                </span>
                <button onClick={() => setEditing(true)} style={{ padding: '5px 10px', background: '#fff3e0', color: '#e65100', border: '1px solid #ffcc80', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                  Edit status
                </button>
              </div>
            )}
          </div>
        </div>

        {score && (
          <div style={{ display: 'flex', gap: 24, alignItems: 'center', padding: '16px 0', borderTop: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0', marginBottom: 16 }}>
            <div style={{ textAlign: 'center', flex: 1 }}>
              <div style={{ fontSize: 13, color: '#888', marginBottom: 4 }}>{fixture.clubs?.name || 'Home'}</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: BLUE }}>{score.home}</div>
              <div style={{ fontSize: 13, color: '#666' }}>({score.hTotal} pts)</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              {fixture.status === 'live' && (
                <div style={{ fontSize: 13, fontWeight: 700, color: '#c62828', marginBottom: 4 }}>
                  {fixture.match_phase === 'ht' ? 'Half Time' :
                   fixture.match_phase === 'ft' ? 'Full Time' :
                   (fixture.clock_half === 2 ? '2nd Half · ' : '1st Half · ') + clockDisplay}
                </div>
              )}
              <div style={{ fontSize: 18, color: '#999' }}>v</div>
            </div>
            <div style={{ textAlign: 'center', flex: 1 }}>
              <div style={{ fontSize: 13, color: '#888', marginBottom: 4 }}>{fixture.opponent || 'Away'}</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#555' }}>{score.away}</div>
              <div style={{ fontSize: 13, color: '#666' }}>({score.aTotal} pts)</div>
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ padding: 16, textAlign: 'center', color: '#999' }}>Loading events...</div>
        ) : (
          <div>
            {scoringEvents.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <SectionLabel>Scorers</SectionLabel>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {scoringEvents.map((e, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, padding: '6px 0', borderBottom: '1px solid #f5f5f5' }}>
                      <span style={{ color: '#999', fontSize: 12, minWidth: 36 }}>{e.minute ? e.minute + "'" : '—'}</span>
                      <span style={{ fontWeight: 600 }}>{e.players?.full_name || 'Unknown'}</span>
                      <span style={{ color: '#666', fontSize: 13 }}>{eventLabel(e.event_type)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {otherEvents.length > 0 && (
              <div>
                <SectionLabel>Other events</SectionLabel>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {otherEvents.map((e, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, padding: '6px 0', borderBottom: '1px solid #f5f5f5' }}>
                      <span style={{ color: '#999', fontSize: 12, minWidth: 36 }}>{e.minute ? e.minute + "'" : '—'}</span>
                      <span style={{ fontWeight: 600 }}>{e.players?.full_name || '—'}</span>
                      <span style={{ color: '#666', fontSize: 13 }}>{eventLabel(e.event_type)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {events.length === 0 && (
              <div style={{ color: '#aaa', fontSize: 14, textAlign: 'center', padding: 16 }}>No events recorded</div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function EmailTab() {
  return <SiteAdminEmailTab />
}

function ComingSoon({ label }) {
  return (
    <div style={{ padding: 48, textAlign: 'center', color: '#aaa', background: '#fafafa', borderRadius: 12, border: '1px dashed #ddd' }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>🚧</div>
      <div style={{ fontSize: 15, fontWeight: 600, color: '#888', marginBottom: 8 }}>Coming soon</div>
      <div style={{ fontSize: 13 }}>{label}</div>
    </div>
  )
}